// Bounded Hachi block art study. No map edits or gameplay state; shared by main and worker painters.
import type { HdBuilding, Rect } from '../../common/src/data';
import type { LightFn } from './hd-registry';
import { canvas } from './canvas';
import { type G, K, HT, px, frame, bands, disc, dk, lt, mix, alpha, text, textW } from './hd-kit';

export const HACHI_STUDY_BUILDINGS = ['ks-bar', 'ks-24h', 'ks-motel'] as const;
export function panel(g:G,x:number,y:number,w:number,h:number,c=K.metalLo){px(g,x,y,w,h,K.ink);bands(g,x+2,y+2,w-4,h-4,lt(c,.12),dk(c,.25),5);frame(g,x+1,y+1,w-2,h-2,K.metal);}
export function label(g:G,t:string,x:number,y:number,w:number,c:string,s=1){panel(g,x,y,w,10+s*7);frame(g,x+2,y+2,w-4,6+s*7,dk(c,.35));text(g,t,x+(w-textW(t,s))/2,y+5,lt(c,.45),s);}
export function fan(g:G,x:number,y:number){panel(g,x,y,30,25);disc(g,x+15,y+12,9,8,K.ink);disc(g,x+15,y+12,7,6,K.metal);for(const d of [-5,0,5])px(g,x+8,y+11+d,14,1,K.metalLo);px(g,x+14,y+5,2,14,K.metalHi);}
export function pipe(g:G,x:number,y:number,h:number){px(g,x,y,5,h,K.ink);px(g,x+1,y,2,h,K.metalHi);for(let k=5;k<h;k+=22)px(g,x-1,y+k,7,3,K.metalLo);}
export function window(g:G,x:number,y:number,w:number,h:number,c:string){panel(g,x,y,w,h);bands(g,x+3,y+3,w-6,h-6,dk(c,.65),dk(c,.22),5);px(g,x+4,y+4,w-8,1,lt(c,.4));px(g,x+w/2,y+2,2,h-4,K.metalLo);px(g,x+4,y+h-5,w-8,2,K.ink);}
function rifle(g:G,x:number,y:number,s=1){px(g,x,y+3*s,8*s,4*s,K.woodHi);px(g,x+7*s,y+2*s,12*s,5*s,K.metalHi);px(g,x+19*s,y+3*s,15*s,2*s,K.metal);px(g,x+11*s,y+7*s,4*s,6*s,K.metalLo);px(g,x+9*s,y,8*s,2*s,K.ink);px(g,x+10*s,y,6*s,1,K.white);}

/** Exactly the previous sprite extent/anchor: foot remains north of the usable counter. */
export function hachiStudyStall(){
 const [cv,g]=canvas(200,162);
 // Structural frame and open tool wall, warm lamps inside a midnight steel shell.
 for(const x of [9,184]){panel(g,x,34,7,120);px(g,x+2,37,1,109,K.metalHi);}
 panel(g,17,44,166,72,dk(K.slate,.24));
 for(let y=49;y<107;y+=5)for(let x=22;x<178;x+=6)px(g,x,y,1,1,K.ink);
 for(const x of [23,104]){px(g,x,52,68,2,K.metalLo);rifle(g,x+1,55,1.6);}
 px(g,20,77,158,3,K.metalHi);px(g,20,80,158,2,K.ink);
 for(let i=0;i<9;i++){const x=23+i*17;panel(g,x,87,13,15,i%3?K.wood:K.metalLo);px(g,x+2,89,9,2,K.amber);text(g,'+',x+4,94,K.white,1);}
 // Recessed service bench, vice, fitted parts and a small terminal.
 panel(g,10,107,180,12,K.metal);px(g,13,108,174,2,K.warmHi);panel(g,24,102,30,7);px(g,33,97,10,6,K.metalHi);
 px(g,62,104,44,3,K.metalHi);px(g,71,102,20,1,K.white);panel(g,150,95,25,14);px(g,153,98,19,8,K.glassHi);px(g,156,100,10,1,K.cyan);
 panel(g,12,119,176,36,K.metalLo);for(const x of [18,67,132,180]){px(g,x,122,2,29,K.ink);for(const y of [123,148])px(g,x+3,y,2,2,K.metalHi);}
 label(g,'HOT LEAD',69,123,61,K.amber);text(g,'MODS',23,129,K.white,1);text(g,'AMMO',145,129,K.white,1);
 for(let x=20;x<185;x+=8)px(g,x,151,4,1,K.amber);
 // Ribbed industrial canopy: no bright candy stripe competing with the lettering.
 bands(g,4,29,192,17,K.metalHi,K.metalLo,5);for(let x=9;x<195;x+=10)px(g,x,31,1,12,K.ink);
 px(g,4,44,192,5,K.ink);px(g,7,46,186,2,K.pink);px(g,18,50,164,2,K.warmHi);
 label(g,'HACHI',33,2,134,K.pink,2);px(g,28,9,3,14,K.metalHi);px(g,169,9,3,14,K.metalHi);
 // Cabling and chunky canopy brackets remain within the original stall footprint.
 for(const x of [18,175]){px(g,x,23,4,9,K.ink);px(g,x,29,9,2,K.metal);}
 return {cv,ax:100,ay:159};
}

/** Only these three existing buildings receive overlays, clipped to their solid footprint. */
export function paintHachiBuilding(g:G,b:HdBuilding,x:number,y:number,L:LightFn){
 if(!(HACHI_STUDY_BUILDINGS as readonly string[]).includes(b.id))return;
 const w=b.w*HT,h=b.h*HT,rh=h-b.face*HT,fy=y+rh;
 g.save();g.beginPath();g.rect(x,y,w,h);g.clip();
 // Service roofs: perimeter maintenance walkways, weathered seams, utility bundles.
 for(let yy=y+28;yy<fy-12;yy+=26){px(g,x+14,yy,w-28,1,alpha(K.metalHi,.18));for(let xx=x+24;xx<x+w-20;xx+=46)px(g,xx,yy-2,3,3,K.metalLo);}
 px(g,x+12,fy-27,w-24,16,dk(K.slate,.48));frame(g,x+12,fy-27,w-24,16,K.metalLo);
 for(let xx=x+18;xx<x+w-14;xx+=10)px(g,xx,fy-25,2,12,K.ink);
 pipe(g,x+w-19,y+16,rh-33);
 // A roof access hut, bundled HVAC and a cable tray give each roof a useful purpose.
 const ux=x+w*.48,uy=y+24;
 panel(g,ux,uy,62,47,b.id==='ks-motel'?K.metal:K.wall);px(g,ux+3,uy+3,56,3,K.concreteHi);
 panel(g,ux+22,uy+22,19,24,K.wallLo);px(g,ux+36,uy+35,2,2,K.warmHi);
 fan(g,ux-40,uy+4);fan(g,ux-40,uy+34);px(g,ux-13,uy+19,13,5,K.metalLo);
 px(g,ux+6,uy+48,45,3,K.ink);for(let xx=ux+8;xx<ux+50;xx+=7)px(g,xx,uy+48,1,3,K.metalHi);
 // Individual roof uses keep these three buildings visually distinct.
 if(b.id==='ks-bar'){
  panel(g,x+18,y+82,116,55,K.woodLo);
  for(let k=0;k<10;k++)px(g,x+22+k*11,y+85,1,49,K.woodHi);
  for(const dx of [38,92]){disc(g,x+dx,y+110,13,8,K.woodHi);disc(g,x+dx,y+109,11,6,K.wood);px(g,x+dx-1,y+104,3,4,K.warmHi);for(const dy of [-15,14])panel(g,x+dx-6,y+dy+107,12,7,K.metalLo);}
  px(g,x+18,y+80,116,2,K.metal);for(let k=0;k<8;k++){px(g,x+22+k*14,y+79,1,7,K.ink);disc(g,x+22+k*14,y+87,2,2,K.warmHi);}
 }else if(b.id==='ks-24h'){
  panel(g,x+22,y+91,85,36,K.metalLo);for(let k=0;k<6;k++)px(g,x+27,y+96+k*4,74,1,K.metalHi);
  px(g,x+104,y+105,49,7,K.metal);px(g,x+150,y+65,7,47,K.metal);for(let k=0;k<4;k++)px(g,x+149,y+73+k*10,9,2,K.metalLo);
 }else{
  for(let k=0;k<3;k++){const sx=x+146+k*43;panel(g,sx,y+88,38,47,K.metalLo);window(g,sx+3,y+91,32,39,K.glassHi);for(let q=0;q<4;q++)px(g,sx+5,y+96+q*8,28,1,K.metalLo);}
 }
 // Repaint just the existing south wall; no façade moves into the lane.
 const hotel=b.id==='ks-motel',bar=b.id==='ks-bar';
 const wall=hotel?mix(K.wall,K.pink,.1):bar?mix(K.wall,K.amber,.08):K.wall;
 bands(g,x+2,fy+2,w-4,h-rh-3,lt(wall,.12),dk(wall,.25),8);
 for(let xx=x+5;xx<x+w-8;xx+=32)px(g,xx,fy+5,2,85,K.ink);
 // Upper windows with an inset balcony and separate AC units.
 for(let xx=x+14;xx<x+w-30;xx+=52){window(g,xx,fy+8,30,23,K.amber);px(g,xx-3,fy+33,36,4,K.metalHi);for(let k=0;k<34;k+=6)px(g,xx-2+k,fy+25,1,9,K.metalLo);}
 px(g,x+3,fy+39,w-6,5,K.ink);px(g,x+4,fy+39,w-8,1,K.metalHi);
 if(bar){
  for(const dx of [12,w/2+8]){window(g,x+dx,fy+56,57,34,K.amber);panel(g,x+dx+65,fy+52,28,41,K.woodLo);px(g,x+dx+68,fy+57,20,21,dk(K.amber,.4));px(g,x+dx+68,fy+80,2,3,K.warmHi);}
  label(g,'YOKOCHO',x+14,fy+43,108,K.amber);label(g,'BAR',x+w/2+10,fy+43,76,K.pink);
  for(const dx of [8,w/2+4,w-14]){px(g,x+dx,fy+58,2,7,K.ink);disc(g,x+dx+1,fy+73,6,9,K.amber);px(g,x+dx,fy+65,2,16,K.warmHi);L(x+dx-5,fy+63,12,20,K.amber,.28,true);}
 }else if(!hotel){
  const door=x+w*.56;window(g,x+9,fy+57,w*.48,33,K.amber);for(let k=0;k<3;k++){px(g,x+12,fy+64+k*8,w*.44,2,K.metalLo);for(let xx=x+15;xx<door-13;xx+=9)px(g,xx,fy+60+k*8,4,4,k===1?K.red:K.warmHi);}
  window(g,door,fy+54,35,40,K.white);px(g,door+16,fy+62,1,26,K.metalLo);
  label(g,'24H MART',x+8,fy+41,w-16,K.cyan);label(g,'24H',x+w-59,fy+66,47,K.cyan);
  L(x+10,fy+47,w-20,5,K.cyan,.23,true);
 }else{
  label(g,'HOTEL',x+10,fy+43,96,K.pink,2);window(g,x+120,fy+56,68,36,K.amber);
  panel(g,x+w-101,fy+49,65,45,K.metalLo);window(g,x+w-96,fy+54,54,38,K.pink);px(g,x+w-69,fy+55,2,37,K.ink);
  for(let k=0;k<3;k++)px(g,x+w-108-k*3,fy+90+k*2,78+k*6,2,K.metalHi);
  label(g,'2089',x+16,fy+73,66,K.pink);
  panel(g,x+w-28,y+92,22,93,K.redDk);frame(g,x+w-26,y+94,18,89,K.pink);
  for(let i=0;i<5;i++)text(g,'HOTEL'[i],x+w-23,y+99+i*16,lt(K.pink,.6),2);
  L(x+12,fy+46,94,16,K.pink,.3,true);
 }
 pipe(g,x+w-7,fy+8,84);px(g,x+3,y+h-3,w-6,2,K.ink);
 g.restore();
}

/** Flat, nonblocking pavement treatments only. No new upright furniture on movement routes. */
export function paintHachiGround(g:G,R:Rect){
 const X=(tx:number)=>(tx-R.x)*HT,Y=(ty:number)=>(ty-R.y)*HT;
 const region={x:49,y:50,w:25,h:12};
 const x0=Math.max(region.x,R.x),y0=Math.max(region.y,R.y),x1=Math.min(region.x+region.w,R.x+R.w),y1=Math.min(region.y+region.h,R.y+R.h);
 if(x1<=x0||y1<=y0)return;
 g.save();g.beginPath();g.rect(X(x0),Y(y0),(x1-x0)*HT,(y1-y0)*HT);g.clip();
 // Existing sidewalks only: recessed drains, expansion joints and restrained neon reflections.
 for(const ty of [54,60])for(let tx=50;tx<74;tx+=4){panel(g,X(tx),Y(ty)+23,36,6,K.metalLo);for(let k=3;k<34;k+=4)px(g,X(tx)+k,Y(ty)+24,1,4,K.ink);}
 for(const [tx,ty,c] of [[58,54,K.pink],[62,54,K.amber],[68,60,K.pink]] as const){
  for(let j=0;j<20;j++){const width=74-j*2;px(g,X(tx)-width/2+(j%3)*3,Y(ty)+j*2,width,1,alpha(c,.12*(1-j/22)));}
 }
 // Repair seams and worn shop-side stencil sit off the marked interaction square.
 for(let tx=52;tx<70;tx+=6){px(g,X(tx),Y(54)+4,52,1,alpha(K.white,.12));px(g,X(tx)+51,Y(54)+4,1,17,K.seam);}
 text(g,'HARDWARE',X(55),Y(53)+17,alpha(K.white,.35),1);
 text(g,'HOT LEAD',X(63),Y(53)+17,alpha(K.white,.35),1);
 g.restore();
}
